import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle, FaSyringe } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { DetailedCitizenVaccinationsIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensVaccinationsIndicatorC3";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC3CitizensVaccinationsTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	dumDate: Column;
	dppDate: Column;
	gestationalAge: Column;
	dtpaVaccination: Column;
};

type IndicatorEsfC3CitizensVaccinationsTableProps = {
	citizensVaccinations: DetailedCitizenVaccinationsIndicatorC3Data[];
	isLoading: boolean;
	openCitizenHistory: (
		citizenWithVaccinations: DetailedCitizenVaccinationsIndicatorC3Data
	) => void;
};

export function getCitizenVaccinationStatusDescription(
	citizenVaccinationStatus?: "finished" | "pending" | "lost"
) {
	switch (citizenVaccinationStatus) {
		case "finished":
			return "Realizado";
		case "pending":
			return "Pendente";
		default:
			return "Fora do prazo";
	}
}

function getIconCitizenVaccinationStatus(
	citizenVaccinationStatus?: "finished" | "pending" | "lost"
) {
	const title = getCitizenVaccinationStatusDescription(citizenVaccinationStatus);
	switch (citizenVaccinationStatus) {
		case "finished":
			return <FaSyringe title={title} className="text-primary fs-2" />;
		case "pending":
			return <FaSyringe title={title} className="text-warning fs-2" />;
		default:
			return <FaSyringe title={title} className="text-muted fs-2" />;
	}
}

export function IndicatorEsfC3CitizensVaccinationsTable({
	citizensVaccinations,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC3CitizensVaccinationsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC3CitizensVaccinationsTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		dumDate: { title: "DUM", visible: true },
		dppDate: { title: "DPP", visible: true },
		gestationalAge: { title: "IG", visible: true },
		dtpaVaccination: { title: "DTPa", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.dumDate.visible && "d-none"}`}>
						{columns.dumDate.title}
					</th>
					<th className={`px-2 ${!columns.dppDate.visible && "d-none"}`}>
						{columns.dppDate.title}
					</th>
					<th className={`px-2 ${!columns.gestationalAge.visible && "d-none"}`}>
						{columns.gestationalAge.title}
					</th>
					<th className={`px-2 ${!columns.dtpaVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.dtpaVaccination.title}
							<CardOverlayTrigger
								header="DTPa"
								message="Aplicações do imunobiológico realizadas durante o período gestacional"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC3CitizensVaccinationsTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC3CitizensVaccinationsTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC3CitizensVaccinationsTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensVaccinations.length > 0
					? citizensVaccinations.map((citizenWithVaccinations, index) => (
							<tr key={`citizen-vaccinations-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithVaccinations.fullName}</div>
										<div className="text-muted">
											{citizenWithVaccinations.birthdate
												? dayjs(citizenWithVaccinations.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithVaccinations.birthdate)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.cpf
										? formatIdentifier(citizenWithVaccinations.cpf)
										: citizenWithVaccinations.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithVaccinations.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithVaccinations.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dumDate.visible && "d-none"
									}`}
								>
									{dayjs(citizenWithVaccinations.dumDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dppDate.visible && "d-none"
									}`}
								>
									{dayjs(citizenWithVaccinations.dppDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.gestationalAge.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.gestationalAge ?? ""}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dtpaVaccination.visible && "d-none"
									}`}
								>
									{getIconCitizenVaccinationStatus(
										citizenWithVaccinations.vaccinationStatus
									)}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithVaccinations)}
										title="Histórico de vacinas da gestante"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
